// Pages/login/recovery.tsx
import React, { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Turnstile from "react-turnstile";
import api from "../../services/api";
import backgroundImage from "../../assets/backgrounds/twitterCover.jpg";
import "./auth.css";

// Clave de sitio Turnstile — pública, embebida en el bundle (build-time).
// Si está vacía, el widget no carga y el form queda sin protección anti-bot
// (modo dev local). En producción setear `REACT_APP_TURNSTILE_SITE_KEY`.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? "";

type TurnstileBound = { reset: () => void };

const PassRecovery: React.FC = () => {
  const [email, setEmail] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  // Ver useLoginLogic.ts: capturamos el bound para resetear el widget
  // después de cada submit (los tokens son single-use; reutilizarlos
  // dispara `timeout-or-duplicate` en Cloudflare).
  const turnstileBoundRef = useRef<TurnstileBound | null>(null);

  // Ver useLoginLogic.ts: deps vacías + functional updates evitan que el
  // widget se re-monte cuando cambia el state del componente padre. Sino
  // los tokens en vuelo se invalidan y login queda intermitente.
  const handleTurnstileSuccess = useCallback(
    (token: string, _preClearance?: boolean, bound?: TurnstileBound) => {
      setTurnstileToken(token);
      if (bound) turnstileBoundRef.current = bound;
      setTurnstileError(null);
    },
    [],
  );

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileError(
      "Error en la verificación de seguridad. Por favor, inténtalo de nuevo.",
    );
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    // Si la site key está configurada, exigimos el token resuelto antes de
    // pegarle al backend. En dev local sin site key, el widget no monta y
    // este check se omite — el backend tampoco valida sin secret.
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setTurnstileError("Por favor, completa la verificación de seguridad.");
      return;
    }

    setIsLoading(true);

    // Round 2026-04-26 fase 3 (P1-G): el backend valida `turnstileToken`
    // contra Cloudflare antes de buscar el email. Si la verificación falla,
    // responde con el mismo mensaje genérico que en éxito (no enumeración).
    const res = await api.recoveryApi.solicitarRecuperacion(
      email,
      turnstileToken ?? "",
    );

    if (res) {
      setSuccessMsg(
        res.message ||
          "Si el correo está registrado, te enviaremos un enlace para restablecer tu contraseña.",
      );
    } else {
      setErrorMsg("No pudimos procesar la solicitud. Inténtalo de nuevo.");
    }

    // El token ya fue consumido por Cloudflare al hacer siteverify.
    // Lo reseteamos para que un segundo submit (ej. el usuario quiere
    // mandar el email de nuevo) genere uno fresco.
    turnstileBoundRef.current?.reset();
    setTurnstileToken(null);
    setIsLoading(false);
  };

  return (
    <div className="login-page">
      <div
        className="login-left-panel"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="login-right-panel">
        <div className="login-container">
          <div className="login-header">
            <h2>Recuperar contraseña</h2>
            <p>
              Ingresa el correo asociado a tu cuenta y te enviaremos un enlace
              para restablecer tu contraseña.
            </p>
          </div>

          {errorMsg && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {errorMsg}
            </div>
          )}

          {successMsg && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              {successMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Correo electrónico</label>
              <div className="input-container">
                <span className="input-icon">✉️</span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nombre@ejemplo.com"
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {TURNSTILE_SITE_KEY && (
              <div className="turnstile-container">
                <Turnstile
                  sitekey={TURNSTILE_SITE_KEY}
                  onSuccess={handleTurnstileSuccess}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpire}
                  theme="light"
                  size="normal"
                  refreshExpired="auto"
                  retry="auto"
                />
                {turnstileError && (
                  <span className="error-text">{turnstileError}</span>
                )}
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={
                isLoading ||
                !email ||
                (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)
              }
            >
              {isLoading ? (
                <span className="loading-container">
                  <div className="spinner-icon"></div>
                  <span className="loading-text">Enviando...</span>
                </span>
              ) : (
                "Enviar enlace de recuperación"
              )}
            </button>
          </form>

          <div className="auth-back-link-wrapper">
            <Link to="/login" className="auth-back-link">
              ← Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassRecovery;
